/**
 * MerakiSwitchPortsPage — port grid editor for a single switch.
 *
 * Two views:
 *   1. Grid (default) — physical port-strip representation; click a port to
 *      open the inline editor on the right.
 *   2. List — sortable table for sysadmins who prefer dense data.
 *
 * Edits are staged client-side, mirrored from the legacy app's
 * "edit then push all at once" flow.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RotateCcw, Save, Tag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MerakiSubNav } from "@/components/app/meraki-sub-nav";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import { useMerakiSwitchPorts } from "@/stores/meraki-switch-ports";
import type { SwitchPort } from "@/api/schemas/meraki";
import { ApiError } from "@/api/client";
import { cn } from "@/lib/cn";

/**
 * Returns the visual treatment for a single port — color + a tiny port-icon.
 * Disabled wins over everything (greyed out); then trunk vs access; then
 * whether the port has a CDP/LLDP neighbor (highlighted).
 */
function portTone(p: SwitchPort): "off" | "trunk" | "access" | "neighbor" {
  if (p.enabled === false) return "off";
  if (p.neighbor) return "neighbor";
  if (p.type === "trunk") return "trunk";
  return "access";
}

export function MerakiSwitchPortsPage() {
  const { data, pending, error, loadFor, stage, reset, apply } = useMerakiSwitchPorts();

  // Currently we wire to a single static device — once the device picker is
  // built (via NetworkPicker → device list), this becomes a route param.
  const deviceSerial = "Q2HW-K3LM-NNNN";
  const deviceName = "MS425-core-08";

  useEffect(() => {
    void loadFor(deviceSerial);
  }, [loadFor]);

  const [view, setView] = useState<"grid" | "list">("grid");
  const [selectedPortId, setSelectedPortId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Merge pending edits over loaded data so the UI shows the working set.
  const merged = useMemo<SwitchPort[]>(
    () =>
      data.map((p) => {
        const e = pending[p.portId];
        return e ? { ...p, ...e } : p;
      }),
    [data, pending],
  );

  const selectedPort = useMemo(
    () => merged.find((p) => p.portId === selectedPortId) ?? null,
    [merged, selectedPortId],
  );

  const stagedCount = Object.keys(pending).length;

  async function onApply() {
    setSaving(true);
    try {
      await apply(deviceSerial);
      toast.success(`Pushed ${stagedCount} port change${stagedCount === 1 ? "" : "s"}`);
    } catch (err) {
      const msg =
        err instanceof ApiError ? `API error ${err.status}` : err instanceof Error ? err.message : "unknown error";
      toast.error("Apply failed", { description: msg });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">Operations</Link>
        <span className="opacity-40">/</span>
        <Link to="/meraki/provisioning" className="text-fg-dim hover:text-fg">Meraki</Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Switch ports</span>
      </div>

      <MerakiSubNav />

      <NoVendorKeysBanner vendor="meraki" />

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">Switch ports</h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            <b className="font-mono text-fg">{deviceName}</b> · {deviceSerial} — click a port to edit. Bulk-apply changes
            with the action bar.
          </p>
        </div>
        <Tabs value={view} onValueChange={(v) => setView(v as "grid" | "list")}>
          <TabsList>
            <TabsTrigger value="grid">Grid</TabsTrigger>
            <TabsTrigger value="list">List</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {error ? (
        <output className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach">
          <AlertTriangle size={16} />
          <span><b className="font-semibold">Showing fixture data.</b> <span className="text-peach/80">{error}</span></span>
        </output>
      ) : null}

      {/* Action bar */}
      <Card>
        <CardContent className="flex items-center justify-between gap-3 py-3">
          <div className="flex items-center gap-3 text-xs text-fg-mute">
            <span><b className="font-mono text-fg">{merged.length}</b> ports</span>
            <span>·</span>
            <span><b className="font-mono text-fg">{merged.filter((p) => p.enabled !== false).length}</b> active</span>
            <span>·</span>
            <span><b className="font-mono text-fg">{merged.filter((p) => p.type === "trunk").length}</b> trunks</span>
            {stagedCount > 0 ? (
              <>
                <span>·</span>
                <Badge variant="info" size="sm">{stagedCount} staged</Badge>
              </>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => reset()} disabled={stagedCount === 0}>
              <RotateCcw size={13} />
              Discard
            </Button>
            <Button size="sm" onClick={() => void onApply()} disabled={stagedCount === 0 || saving}>
              <Save size={13} />
              {saving ? "Pushing…" : `Apply ${stagedCount > 0 ? `(${stagedCount})` : ""}`}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Body — grid OR list based on view toggle */}
      {view === "grid" ? (
        <div className="grid grid-cols-[1fr_360px] gap-5">
          <Card>
            <CardContent>
              <CardTitle>Ports 1 – {merged.length}</CardTitle>
              <CardSub className="mt-1 mb-4">
                Click a port to edit · double-click the panel to deselect
              </CardSub>
              <div
                className="grid gap-2"
                style={{ gridTemplateColumns: "repeat(12, minmax(0, 1fr))" }}
                onDoubleClick={() => setSelectedPortId(null)}
              >
                {merged.map((p) => {
                  const tone = portTone(p);
                  const isSelected = p.portId === selectedPortId;
                  const isDirty = !!pending[p.portId];
                  return (
                    <button
                      key={p.portId}
                      type="button"
                      onClick={() => setSelectedPortId(p.portId)}
                      title={`Port ${p.portId} — VLAN ${p.vlan ?? "—"}`}
                      className={cn(
                        "group relative flex flex-col items-center gap-1 rounded-lg border px-1.5 py-2 text-center transition-colors",
                        tone === "off" && "border-line bg-surface-tint/50 text-fg-mute",
                        tone === "access" && "border-mint/30 bg-mint/[0.07] text-mint",
                        tone === "trunk" && "border-violet/40 bg-violet/[0.1] text-violet",
                        tone === "neighbor" && "border-cyan/40 bg-cyan/[0.1] text-cyan",
                        isSelected && "ring-2 ring-violet/70",
                      )}
                    >
                      <div className="font-mono text-[10px] text-fg-mute">{p.portId}</div>
                      <div className="h-3 w-6 rounded-sm bg-current/30">
                        <div className="m-[2px] h-1 rounded-sm bg-current/80" />
                      </div>
                      <div className="font-mono text-[9px]">{p.vlan ?? (p.type === "trunk" ? "trk" : "—")}</div>
                      {isDirty ? (
                        <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-violet ring-2 ring-bg-0" />
                      ) : null}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-5 flex flex-wrap items-center gap-4 border-t border-line pt-3 text-[11px] text-fg-mute">
                <span className="inline-flex items-center gap-2">
                  <span className="h-3 w-3 rounded-sm border border-mint/30 bg-mint/[0.18]" />Access
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="h-3 w-3 rounded-sm border border-violet/40 bg-violet/[0.18]" />Trunk
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="h-3 w-3 rounded-sm border border-cyan/40 bg-cyan/[0.18]" />CDP/LLDP neighbor
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="h-3 w-3 rounded-sm border border-line bg-surface-tint" />Disabled
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Right rail — selected port editor */}
          <Card>
            <CardContent>
              {selectedPort ? (
                <PortEditor port={selectedPort} onChange={(c) => stage(selectedPort.portId, c)} />
              ) : (
                <div className="flex flex-col items-center gap-2 py-12 text-center text-sm text-fg-mute">
                  <Tag size={20} className="text-violet" />
                  <CardTitle>No port selected</CardTitle>
                  <CardSub>Click a port on the left to inspect or edit it.</CardSub>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card>
          <CardContent>
            <PortList ports={merged} pending={pending} onStage={(portId, c) => stage(portId, c)} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- */
/* Port editor — used in the grid view's right rail.              */
/* -------------------------------------------------------------- */

function PortEditor({
  port,
  onChange,
}: {
  port: SwitchPort;
  onChange: (change: Partial<SwitchPort>) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <CardTitle>Port {port.portId}</CardTitle>
        <CardSub className="mt-0.5">
          {port.deviceName} · {port.deviceSerial}
        </CardSub>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="p-name">Friendly name</Label>
        <Input
          id="p-name"
          value={port.name ?? ""}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="(unnamed)"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="p-vlan">Access VLAN</Label>
          <Input
            id="p-vlan"
            value={port.vlan ?? ""}
            onChange={(e) => onChange({ vlan: e.target.value || null })}
            placeholder="(none)"
            disabled={port.type === "trunk"}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Type</Label>
          <div className="grid grid-cols-2 gap-2">
            {(["access", "trunk"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => onChange({ type: t })}
                className={cn(
                  "rounded-lg border px-2 py-1.5 text-xs font-semibold capitalize transition-colors",
                  port.type === t
                    ? "border-violet/50 bg-aurora-soft text-violet"
                    : "border-line bg-surface text-fg-dim hover:bg-surface-2",
                )}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Tags</Label>
        <Input
          value={(port.tags ?? []).join(", ")}
          onChange={(e) =>
            onChange({
              tags: e.target.value
                .split(",")
                .flatMap((t) => { const v = t.trim(); return v ? [v] : []; }),
            })
          }
          placeholder="edge, guest, …"
        />
      </div>

      <label className="mt-1 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={port.enabled !== false}
          onChange={(e) => onChange({ enabled: e.target.checked })}
          className="accent-violet"
        />
        <span className="text-fg-dim">Port enabled</span>
      </label>

      {port.neighbor ? (
        <div className="rounded-lg border border-cyan/30 bg-cyan/[0.08] p-3 text-xs text-fg-dim">
          <div className="font-mono text-cyan">CDP/LLDP neighbor</div>
          <div className="mt-1">
            <b className="text-fg">{port.neighbor.systemName}</b> on port{" "}
            <span className="font-mono">{port.neighbor.portId}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------- */
/* List view — denser table representation of the same ports.     */
/* -------------------------------------------------------------- */

function PortList({
  ports,
  pending,
  onStage,
}: {
  ports: SwitchPort[];
  pending: Record<string, Partial<SwitchPort>>;
  onStage: (portId: string, change: Partial<SwitchPort>) => void;
}) {
  return (
    <div className="grid grid-cols-[60px_1fr_120px_120px_140px_80px] items-center gap-2 text-xs">
      <div className="font-mono text-[10px] uppercase tracking-wider text-fg-mute">Port</div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-fg-mute">Name</div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-fg-mute">Type</div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-fg-mute">VLAN</div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-fg-mute">Tags</div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-fg-mute">Enabled</div>

      {ports.map((p) => {
        const dirty = !!pending[p.portId];
        return (
          <PortListRowFragment key={p.portId} p={p} dirty={dirty} onStage={onStage} />
        );
      })}
    </div>
  );
}

/** A single row in the list view — written as a fragment so the parent
 *  grid layout owns column widths. */
function PortListRowFragment({
  p,
  dirty,
  onStage,
}: {
  p: SwitchPort;
  dirty: boolean;
  onStage: (portId: string, change: Partial<SwitchPort>) => void;
}) {
  const cellCls = cn(
    "border-t border-line/60 py-2",
    dirty && "bg-aurora-soft/30",
  );
  return (
    <>
      <div className={cn(cellCls, "font-mono text-[11px] text-fg")}>{p.portId}</div>
      <div className={cellCls}>
        <Input
          value={p.name ?? ""}
          onChange={(e) => onStage(p.portId, { name: e.target.value })}
          className="h-7 px-2 text-[11px]"
          placeholder="(unnamed)"
        />
      </div>
      <div className={cellCls}>
        <Badge variant={p.type === "trunk" ? "info" : "ok"} size="sm">{p.type}</Badge>
      </div>
      <div className={cellCls}>
        <Input
          value={p.vlan ?? ""}
          onChange={(e) => onStage(p.portId, { vlan: e.target.value || null })}
          className="h-7 px-2 text-[11px]"
          placeholder="—"
          disabled={p.type === "trunk"}
        />
      </div>
      <div className={cn(cellCls, "font-mono text-[10.5px] text-fg-mute")}>
        {(p.tags ?? []).join(", ") || "—"}
      </div>
      <div className={cellCls}>
        <input
          type="checkbox"
          aria-label={`Port ${p.portId} enabled`}
          checked={p.enabled !== false}
          onChange={(e) => onStage(p.portId, { enabled: e.target.checked })}
          className="accent-violet"
        />
      </div>
    </>
  );
}
